import { Fragment } from "react";

import { Divider } from "@mui/material";
import { Column, Image, Row, SectionedCard, StatColumn, StatProps, Text, TextExternalLink } from "@bridgesplit/ui";
import {
    EarnExponentAccount,
    EarnVaultRecordView,
    LendingVaultMetadataApi,
    REWARDS_TICK,
    useLiveClockMs
} from "@bridgesplit/abf-react";
import { explorerAccountUrl, formatAddress, formatDate, formatPercent, formatSeconds } from "@bridgesplit/utils";
import { formatWithdrawalSchedule } from "app/utils/domain/earn";

const WITHDRAWAL_WINDOW_TOOLTIP =
    "Withdrawals become claimable at the scheduled times shown, in your local timezone. The manager can fill a request at any time, but you can only claim once its unlock time has passed. After that it stays claimable with no deadline. A request made within 4 hours before a scheduled time will instead unlock at the following one.";

export function AddressValue({ address, href }: { address: string; href: string }) {
    return (
        <TextExternalLink href={href} underlineLink>
            {formatAddress(address)}
        </TextExternalLink>
    );
}

export default function EarnExponentCuratorCard({
    vault,
    account
}: {
    vault: EarnVaultRecordView;
    account: EarnExponentAccount;
}) {
    const metadata = vault.metadataJson;
    const nowMs = useLiveClockMs(REWARDS_TICK);

    const { squadsVault, authorities, reservesConfig, proposalVoteConfig, normalWithdrawalCutBp } = account;
    const { sentinel, allocator } = authorities;

    const configStats: StatProps[] = [
        {
            caption: "Curator",
            value: <CuratorName metadata={metadata} />
        },
        {
            caption: "Vault address",
            value: squadsVault && <AddressValue address={squadsVault} href={explorerAccountUrl(squadsVault)} />,
            hide: !squadsVault
        },
        {
            caption: "Strategist admin",
            value: allocator && <AddressValue address={allocator} href={explorerAccountUrl(allocator)} />,
            hide: !allocator
        },
        {
            caption: "Sentinel",
            value: sentinel && <AddressValue address={sentinel} href={explorerAccountUrl(sentinel)} />,
            hide: !sentinel
        },
        {
            caption: "Infrastructure",
            value: "Squads"
        },
        {
            caption: "Vault deployment date",
            value: formatDate(vault.createdAt, "date"),
            hide: !vault.createdAt
        }
    ];

    const feeStats: StatProps[] = [
        {
            caption: "Management fee",
            tooltip:
                "An annual fee charged on the vault's total assets under management. The fee accrues continuously over time and is collected periodically by the manager.",
            value: formatPercent(reservesConfig?.managementFeeBps),
            hide: reservesConfig?.managementFeeBps === undefined
        },
        {
            caption: "Withdrawal fee",
            tooltip: "A fee applied when the manager fills a queued withdrawal.",
            value: formatPercent(normalWithdrawalCutBp),
            hide: normalWithdrawalCutBp === undefined
        },
        {
            caption: "Instant withdrawal fee",
            tooltip:
                "An additional fee for withdrawing directly from vault reserves without waiting for the manager to fill the request. This fee is added on top of the standard withdrawal fee.",
            value: formatPercent(reservesConfig?.fastWithdrawalCutBp),
            hide: reservesConfig?.fastWithdrawalCutBp === undefined
        },
        {
            caption: "Withdrawal window",
            tooltip: WITHDRAWAL_WINDOW_TOOLTIP,
            value: formatWithdrawalSchedule(reservesConfig?.withdrawalPeriod, nowMs),
            hide: reservesConfig?.withdrawalPeriod === undefined
        }
    ];

    const governanceStats: StatProps[] = [
        {
            caption: "Vault parameter change",
            tooltip:
                "The timelock duration applied to approved governance proposals before execution. This delay is set at vault creation and can only be modified through governance.",
            value: `${formatSeconds(proposalVoteConfig?.defaultTimelockSeconds, { maxUnit: "day" })} timelock`,
            hide: proposalVoteConfig?.defaultTimelockSeconds === undefined
        },
        {
            caption: "Voting period",
            tooltip: "The window during which depositors can vote to reject a proposal.",
            value: formatSeconds(proposalVoteConfig?.defaultVotingPeriodSeconds, { maxUnit: "day" }),
            hide: proposalVoteConfig?.defaultVotingPeriodSeconds === undefined
        },
        {
            caption: "Rejection threshold",
            tooltip:
                "The share of total deposits that must vote to reject a proposal before its voting period ends in order to block it.",
            value: formatPercent(proposalVoteConfig?.rejectionThresholdBps),
            hide: proposalVoteConfig?.rejectionThresholdBps === undefined
        }
    ];

    const sections = [
        { title: "Configuration", stats: configStats },
        { title: "Fees", stats: feeStats },
        { title: "Governance", stats: governanceStats }
    ].filter((section) => section.stats.some((stat) => !stat.hide));

    return (
        <SectionedCard>
            <Column spacing={2}>
                {sections.map((section, i) => (
                    <Fragment key={section.title}>
                        {i > 0 && <Divider />}
                        <Column spacing={1}>
                            <Text variant="h4">{section.title}</Text>
                            <StatColumn stats={section.stats} />
                        </Column>
                    </Fragment>
                ))}
            </Column>
        </SectionedCard>
    );
}

function CuratorName({ metadata }: { metadata: LendingVaultMetadataApi | undefined }) {
    return (
        <Row spacing={0.5}>
            <Image variant="circle" src={metadata?.managerImage} size="20px" />
            <Text>{metadata?.managerName}</Text>
        </Row>
    );
}
